import { useConfiguration } from '../../../../store/configurationStore';
import {
  Rule,
  Label,
  RuleColumn,
  FeeTypeCategory,
  BuySellCategory,
  DepositWithdrawCategory,
  FxSpotCategory,
  SettlementAccountCategory,
  DeductionRebateCategory,
  SectionId,
  SecurityType,
} from '../types';

export const useCategoryRulesEngine = (
  sectionId: SectionId,
  categoryId:
    | FeeTypeCategory
    | BuySellCategory
    | DepositWithdrawCategory
    | FxSpotCategory
    | SettlementAccountCategory
    | DeductionRebateCategory
) => {
  const {
    templates,
    activeTemplateId,
    activeInstrumentType,
    addCategoryRule,
    updateCategoryRule,
    deleteCategoryRule,
    addColumnToCategoryRule,
    removeColumnFromCategoryRule,
    updateCategoryRuleColumn,
    addLabelToCategoryRuleColumn,
    removeLabelFromCategoryRuleColumn,
  } = useConfiguration();

  // Get the active template and instrument config
  const activeTemplate = templates.find(t => t.id === activeTemplateId) || null;
  const activeInstrumentConfig = activeTemplate?.instrumentConfigs.find(
    ic => ic.instrumentType === activeInstrumentType
  );

  // Get the section with category rules
  const section = activeInstrumentConfig?.sections.find(
    s => s.id === sectionId
  );

  // Get rules for this specific category
  const categorySection = section?.config.categoryRules?.find(
    cs => cs.categoryId === categoryId
  );
  const rules = categorySection?.rules || [];

  // Create a new rule for this category with one empty column
  const createRule = (name: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    const newRule: Rule = {
      id: `rule-${categoryId}-${Date.now()}`,
      name,
      columns: [
        {
          id: `col-${Date.now()}-${Math.random()}`,
          sourceColumn: '',
          labels: [],
        },
      ],
      conditions: [],
    };

    addCategoryRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      newRule
    );
  };

  // Update an existing rule for this category
  const updateRuleDetails = (ruleId: string, updates: Partial<Rule>): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    updateCategoryRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId,
      updates
    );
  };

  // Delete a rule from this category
  const removeRule = (ruleId: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    // Prevent deletion if only one rule exists
    if (rules.length <= 1) {
      console.warn(
        '⚠️ Cannot delete the last rule. At least one rule must exist.'
      );
      return;
    }

    deleteCategoryRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId
    );
  };

  // Add a new column to a rule
  const addColumn = (ruleId: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    const newColumn: RuleColumn = {
      id: `col-${Date.now()}-${Math.random()}`,
      sourceColumn: '',
      labels: [],
    };

    addColumnToCategoryRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId,
      newColumn
    );
  };

  // Add a Security Type condition column to a rule
  const addSecurityTypeColumn = (ruleId: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    const newColumn: RuleColumn = {
      id: `col-${Date.now()}-${Math.random()}`,
      columnType: 'SECURITY_TYPE',
      securityType: 'OTC',
      sourceColumn: '',
      labels: [],
    };

    addColumnToCategoryRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId,
      newColumn
    );
  };

  // Update the securityType on a SECURITY_TYPE column
  const updateColumnSecurityType = (ruleId: string, columnId: string, securityType: SecurityType): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    updateCategoryRuleColumn(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId,
      columnId,
      { securityType }
    );
  };

  // Remove a column from a rule
  const removeColumn = (ruleId: string, columnId: string): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    removeColumnFromCategoryRule(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId,
      columnId
    );
  };

  // Update column source
  const updateColumnSource = (
    ruleId: string,
    columnId: string,
    sourceColumn: string
  ): void => {
    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    updateCategoryRuleColumn(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId,
      columnId,
      { sourceColumn }
    );
  };

  // Add label to a specific column in a rule
  const addLabelToColumn = (
    ruleId: string,
    columnId: string,
    value: string
  ): void => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) {
      throw new Error(`⚠️ Rule with id "${ruleId}" not found`);
    }

    const column = rule.columns.find(c => c.id === columnId);
    if (!column) {
      throw new Error(`⚠️ Column with id "${columnId}" not found`);
    }

    const isDuplicate = column.labels.some(
      label => label.value.toLowerCase() === value.toLowerCase()
    );

    if (isDuplicate) {
      throw new Error(`⚠️ Label "${value}" already exists in this column`);
    }

    const newLabel: Label = {
      id: `label-${Date.now()}-${Math.random()}`,
      value,
    };

    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    // Use new dedicated action - it reads current state inside set()
    addLabelToCategoryRuleColumn(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId,
      columnId,
      newLabel
    );
  };

  // Remove label from a specific column in a rule
  const removeLabelFromColumn = (
    ruleId: string,
    columnId: string,
    labelId: string
  ): void => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) {
      throw new Error(`⚠️ Rule with id "${ruleId}" not found`);
    }

    const column = rule.columns.find(c => c.id === columnId);
    if (!column) {
      throw new Error(`⚠️ Column with id "${columnId}" not found`);
    }

    const labelExists = column.labels.some(label => label.id === labelId);
    if (!labelExists) {
      throw new Error(`⚠️ Label with id "${labelId}" not found in column`);
    }

    if (!activeTemplateId || !activeInstrumentType) {
      throw new Error('⚠️ No active template or instrument type selected');
    }

    // Use new dedicated action - it reads current state inside set()
    removeLabelFromCategoryRuleColumn(
      activeTemplateId,
      activeInstrumentType,
      sectionId,
      categoryId,
      ruleId,
      columnId,
      labelId
    );
  };

  // Validate rule completeness - checks if columns have sources and labels
  const validateRule = (
    ruleId: string
  ): { isValid: boolean; warnings: string[] } => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) {
      return { isValid: false, warnings: ['Rule not found'] };
    }

    const warnings: string[] = [];

    // Check if any column has empty sourceColumn
    const emptyColumns = rule.columns.filter(
      col => !col.sourceColumn || col.sourceColumn.trim() === ''
    );
    if (emptyColumns.length > 0) {
      warnings.push('⚠️ Some columns have no source selected');
    }

    // Check if any column has no labels
    const columnsWithoutLabels = rule.columns.filter(
      col => col.labels.length === 0
    );
    if (columnsWithoutLabels.length > 0) {
      warnings.push('⚠️ Some columns have no labels');
    }

    // Check for duplicate column selections
    const columnSources = rule.columns
      .map(col => col.sourceColumn)
      .filter(source => source && source.trim() !== '');
    const duplicates = columnSources.filter(
      (source, index) => columnSources.indexOf(source) !== index
    );
    if (duplicates.length > 0) {
      warnings.push('⚠️ Some columns are selected multiple times');
    }

    // Rule is valid if it has at least one complete column (with source and labels)
    const hasCompleteColumn = rule.columns.some(
      col => col.sourceColumn.trim() !== '' && col.labels.length > 0
    );

    return { isValid: hasCompleteColumn, warnings };
  };

  return {
    rules,
    createRule,
    updateRuleDetails,
    removeRule,
    addColumn,
    addSecurityTypeColumn,
    updateColumnSecurityType,
    removeColumn,
    updateColumnSource,
    addLabelToColumn,
    removeLabelFromColumn,
    validateRule,
  };
};
